import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Button, Form, Modal } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faCheck,
  faLayerGroup,
  faSeedling,
  faTrophy,
  faImage,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

import {
  white,
  squidInk,
  coolGrey,
  blackberry,
  grape,
  softGreen,
  softAmber,
  greyStroke,
  backgroundGrey5,
  positiveGreen,
  softBlueLight,
  softGreenLight,
  softAmberLight,
  softPurpleLight,
} from 'lib/css/colors';

// Category icons and colors - use color constants from lib/css/colors.js
const CATEGORY_CONFIGS = {
  'Default Widgets': {
    icon: faLayerGroup,
    color: grape,
    bgColor: softBlueLight,
  },
  'Brand Harvesting Widgets': {
    icon: faSeedling,
    color: softGreen,
    bgColor: softGreenLight,
  },
  'Brand QA Widgets': {
    icon: faTrophy,
    color: softAmber,
    bgColor: softAmberLight,
  },
  'Thumbnail Assigning Stats': {
    icon: faImage,
    color: grape,
    bgColor: softPurpleLight,
  },
};

function WidgetSelectionModal({ show, onHide, widgetOptions, activeWidgets, setActiveWidgets }) {
  const [selectedWidgets, setSelectedWidgets] = useState(activeWidgets);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(widgetOptions.map(g => g.category));

  const filteredOptions = useMemo(() => {
    if (!searchQuery.trim()) return widgetOptions;

    const query = searchQuery.toLowerCase();
    return widgetOptions
      .map(group => ({
        ...group,
        options: group.options.filter(option => option.label.toLowerCase().includes(query)),
      }))
      .filter(group => group.options.length > 0);
  }, [widgetOptions, searchQuery]);

  const handleWidgetToggle = widgetType => {
    if (selectedWidgets.includes(widgetType)) {
      setSelectedWidgets(selectedWidgets.filter(w => w !== widgetType));
    } else {
      setSelectedWidgets([...selectedWidgets, widgetType]);
    }
  };

  const handleSave = () => {
    setActiveWidgets(selectedWidgets);
    onHide();
  };

  const handleSelectAll = filteredCategoryOptions => {
    const allValues = filteredCategoryOptions.map(o => o.value);
    const allSelected = allValues.every(v => selectedWidgets.includes(v));

    if (allSelected) {
      setSelectedWidgets(selectedWidgets.filter(w => !allValues.includes(w)));
    } else {
      const newWidgets = [...new Set([...selectedWidgets, ...allValues])];
      setSelectedWidgets(newWidgets);
    }
  };

  const toggleCategory = category => {
    if (expandedCategories.includes(category)) {
      setExpandedCategories(expandedCategories.filter(c => c !== category));
    } else {
      setExpandedCategories([...expandedCategories, category]);
    }
  };

  const selectedCount = selectedWidgets.length;
  const totalCount = widgetOptions.reduce((sum, g) => sum + g.options.length, 0);

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <div className={css(styles.modalContent)}>
        {/* Header */}
        <div className={css(styles.header)}>
          <div className={css(styles.headerLeft)}>
            <h2 className={css(styles.title)}>Customize Dashboard</h2>
            <p className={css(styles.subtitle)}>
              Select the widgets you want to display on your dashboard
            </p>
          </div>
          <button onClick={onHide} className={css(styles.closeButton)} aria-label="Close modal">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Search and stats bar */}
        <div className={css(styles.searchBar)}>
          <div className={css(styles.searchInputContainer)}>
            <FontAwesomeIcon icon={faSearch} className={css(styles.searchIcon)} />
            <Form.Control
              type="text"
              placeholder="Search widgets..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className={css(styles.searchInput)}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} className={css(styles.clearSearch)}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            )}
          </div>
          <div className={css(styles.selectedBadge)}>
            <span className={css(styles.selectedCount)}>{selectedCount}</span>
            <span className={css(styles.selectedLabel)}>of {totalCount} selected</span>
          </div>
        </div>

        {/* Widget categories */}
        <div className={css(styles.body)}>
          {filteredOptions.map(group => {
            const config = CATEGORY_CONFIGS[group.category] || CATEGORY_CONFIGS['Default Widgets'];
            const isExpanded = expandedCategories.includes(group.category);
            const selectedInCategory = group.options.filter(o =>
              selectedWidgets.includes(o.value),
            ).length;

            return (
              <div key={group.category} className={css(styles.categorySection)}>
                {/* Category header */}
                <div
                  className={css(styles.categoryHeader)}
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onClick={() => toggleCategory(group.category)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleCategory(group.category);
                    }
                  }}
                >
                  <div className={css(styles.categoryLeft)}>
                    <div
                      className={css(styles.categoryIcon)}
                      style={{ backgroundColor: config.bgColor }}
                    >
                      <FontAwesomeIcon icon={config.icon} style={{ color: config.color }} />
                    </div>
                    <div className={css(styles.categoryInfo)}>
                      <span className={css(styles.categoryName)}>{group.category}</span>
                      <span className={css(styles.categoryCount)}>
                        {selectedInCategory} of {group.options.length} selected
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={e => {
                      e.stopPropagation();
                      handleSelectAll(group.options);
                    }}
                    className={css(styles.selectAllButton)}
                  >
                    {selectedInCategory === group.options.length ? 'Deselect All' : 'Select All'}
                  </button>
                </div>

                {/* Widget options */}
                {isExpanded && (
                  <div className={css(styles.widgetGrid)}>
                    {group.options.map(option => {
                      const isSelected = selectedWidgets.includes(option.value);
                      return (
                        <div
                          key={option.value}
                          className={css(
                            styles.widgetCard,
                            isSelected && styles.widgetCardSelected,
                          )}
                          role="checkbox"
                          tabIndex={0}
                          aria-checked={isSelected}
                          onClick={() => handleWidgetToggle(option.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              handleWidgetToggle(option.value);
                            }
                          }}
                        >
                          <div
                            className={css(styles.checkbox, isSelected && styles.checkboxSelected)}
                            style={
                              isSelected
                                ? { backgroundColor: config.color, borderColor: config.color }
                                : {}
                            }
                          >
                            {isSelected && (
                              <FontAwesomeIcon icon={faCheck} className={css(styles.checkIcon)} />
                            )}
                          </div>
                          <span className={css(styles.widgetLabel)}>{option.label}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {filteredOptions.length === 0 && (
            <div className={css(styles.noResults)}>
              <p>No widgets found matching &quot;{searchQuery}&quot;</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={css(styles.footer)}>
          <Button variant="outline-secondary" onClick={onHide} className={css(styles.cancelButton)}>
            Cancel
          </Button>
          <Button onClick={handleSave} className={css(styles.saveButton)}>
            <FontAwesomeIcon icon={faCheck} className={css(styles.saveIcon)} />
            Save Changes
          </Button>
        </div>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalContent: {
    backgroundColor: white,
    borderRadius: 16,
    overflow: 'hidden',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '24px 24px 16px',
    borderBottom: `1px solid ${greyStroke}`,
  },
  headerLeft: {
    flex: 1,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: squidInk,
    margin: 0,
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    color: coolGrey,
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    color: coolGrey,
    fontSize: 18,
    cursor: 'pointer',
    padding: 8,
    marginRight: -8,
    marginTop: -8,
    borderRadius: 8,
    transition: 'all 0.15s ease',
    ':hover': {
      backgroundColor: backgroundGrey5,
      color: squidInk,
    },
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: '16px 24px',
    backgroundColor: backgroundGrey5,
  },
  searchInputContainer: {
    flex: 1,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  searchIcon: {
    position: 'absolute',
    left: 14,
    color: coolGrey,
    fontSize: 14,
  },
  searchInput: {
    paddingLeft: 40,
    paddingRight: 36,
    height: 42,
    borderRadius: 8,
    border: `1px solid ${greyStroke}`,
    fontSize: 14,
    ':focus': {
      borderColor: grape,
      boxShadow: `0 0 0 3px ${grape}20`,
    },
  },
  clearSearch: {
    position: 'absolute',
    right: 12,
    background: 'none',
    border: 'none',
    color: coolGrey,
    cursor: 'pointer',
    padding: 4,
    fontSize: 12,
    ':hover': {
      color: squidInk,
    },
  },
  selectedBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 14px',
    backgroundColor: white,
    borderRadius: 20,
    border: `1px solid ${greyStroke}`,
  },
  selectedCount: {
    fontSize: 16,
    fontWeight: 700,
    color: grape,
  },
  selectedLabel: {
    fontSize: 13,
    color: coolGrey,
  },
  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 24px',
  },
  categorySection: {
    marginBottom: 20,
    ':last-child': {
      marginBottom: 0,
    },
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    backgroundColor: backgroundGrey5,
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    ':hover': {
      backgroundColor: `${grape}10`,
    },
  },
  categoryLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  categoryIcon: {
    width: 36,
    height: 36,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 16,
  },
  categoryInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  categoryName: {
    fontSize: 15,
    fontWeight: 600,
    color: squidInk,
  },
  categoryCount: {
    fontSize: 12,
    color: coolGrey,
  },
  selectAllButton: {
    background: 'none',
    border: 'none',
    color: grape,
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    padding: '6px 12px',
    borderRadius: 6,
    transition: 'all 0.15s ease',
    ':hover': {
      backgroundColor: `${grape}15`,
    },
  },
  widgetGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
    gap: 10,
    marginTop: 12,
    paddingLeft: 8,
  },
  widgetCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    backgroundColor: white,
    border: `1px solid ${greyStroke}`,
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    ':hover': {
      borderColor: grape,
      backgroundColor: `${grape}15`,
    },
  },
  widgetCardSelected: {
    borderColor: positiveGreen,
    backgroundColor: `${positiveGreen}15`,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 5,
    border: `2px solid ${greyStroke}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'all 0.15s ease',
  },
  checkboxSelected: {
    // Colors applied inline based on category
  },
  checkIcon: {
    color: white,
    fontSize: 10,
  },
  widgetLabel: {
    fontSize: 13,
    color: squidInk,
    lineHeight: 1.3,
  },
  noResults: {
    textAlign: 'center',
    padding: '40px 20px',
    color: coolGrey,
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 12,
    padding: '16px 24px',
    borderTop: `1px solid ${greyStroke}`,
    backgroundColor: backgroundGrey5,
  },
  cancelButton: {
    padding: '10px 20px',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 14,
  },
  saveButton: {
    backgroundColor: blackberry,
    border: 'none',
    padding: '10px 24px',
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 14,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    transition: 'all 0.2s ease',
    ':hover': {
      backgroundColor: grape,
      transform: 'translateY(-1px)',
    },
  },
  saveIcon: {
    fontSize: 12,
  },
});

WidgetSelectionModal.propTypes = {
  show: PropTypes.bool,
  widgetOptions: PropTypes.array,
  activeWidgets: PropTypes.array,
  onHide: PropTypes.func,
  setActiveWidgets: PropTypes.func,
};

export default WidgetSelectionModal;
